import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  KeyIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  UserPlusIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
  BellAlertIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
  VideoCameraIcon,
  PlayCircleIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  GiftIcon,
  BanknotesIcon,
  PresentationChartLineIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useMemo } from 'react';
import { useModerationCounts } from '@/hooks/useRealtime';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: { category: string; action: string };
  badgeKey?: 'conversationFlags' | 'reviewFlags' | 'businessModeration' | 'total';
}

export default function Sidebar() {
  const { sidebarCollapsed, sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore();
  const { role, hasPermission } = useAuthStore();
  const { counts } = useModerationCounts();

  const navigation = useMemo<NavItem[]>(() => [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Users', href: '/users', icon: UsersIcon, permission: { category: 'users', action: 'read' } },
    { name: 'Businesses', href: '/businesses', icon: BuildingStorefrontIcon, permission: { category: 'businesses', action: 'read' }, badgeKey: 'businessModeration' },
    { name: 'Preset Profiles', href: '/preset-profiles', icon: UserPlusIcon, permission: { category: 'businesses', action: 'write' } },
    { name: 'Business Controls', href: '/business-controls', icon: WrenchScrewdriverIcon, permission: { category: 'businesses', action: 'write' } },
    { name: 'Blog', href: '/blog', icon: DocumentTextIcon, permission: { category: 'blog', action: 'read' } },
    { name: 'Generate Blogs', href: '/blog/generate', icon: SparklesIcon, permission: { category: 'blog', action: 'write' } },
    { name: 'Duplicates', href: '/blog/duplicates', icon: DocumentDuplicateIcon, permission: { category: 'blog', action: 'write' } },
    { name: 'Blog Authors', href: '/blog-authors', icon: UserCircleIcon, permission: { category: 'blog', action: 'write' } },
    { name: 'Email Templates', href: '/email-templates', icon: EnvelopeIcon, permission: { category: 'email_templates', action: 'read' } },
    { name: 'Moderation', href: '/moderation', icon: ShieldExclamationIcon, permission: { category: 'moderation', action: 'read' }, badgeKey: 'total' },
    { name: 'Operations', href: '/operations', icon: ExclamationTriangleIcon, permission: { category: 'analytics', action: 'read' } },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, permission: { category: 'analytics', action: 'read' } },
    { name: 'Google Analytics', href: '/google-analytics', icon: GlobeAltIcon, permission: { category: 'analytics', action: 'read' } },
    { name: 'SEO & Indexing', href: '/seo-indexing', icon: MagnifyingGlassIcon, permission: { category: 'analytics', action: 'read' } },
    { name: 'Payments', href: '/payments', icon: CreditCardIcon, permission: { category: 'payments', action: 'read' } },
    { name: 'Lead Billing', href: '/lead-billing', icon: CurrencyDollarIcon, permission: { category: 'payments', action: 'read' } },
    { name: 'Access Codes', href: '/access-codes', icon: KeyIcon, permission: { category: 'access_codes', action: 'read' } },
    { name: 'Promo Codes', href: '/promo-codes', icon: TagIcon, permission: { category: 'promo_codes', action: 'read' } },
    { name: 'Journey Videos', href: '/journey-videos', icon: VideoCameraIcon, permission: { category: 'businesses', action: 'read' } },
    { name: 'How-To Videos', href: '/how-to-videos', icon: PlayCircleIcon, permission: { category: 'blog', action: 'write' } },
    { name: 'A La Carte', href: '/a-la-carte', icon: ShoppingCartIcon, permission: { category: 'settings', action: 'write' } },
    { name: 'Affiliates', href: '/affiliates', icon: UserGroupIcon, permission: { category: 'affiliates', action: 'read' } },
    { name: 'Feedback', href: '/feedback', icon: ChatBubbleLeftRightIcon, permission: { category: 'moderation', action: 'read' } },
    { name: 'Waitlist', href: '/waitlist', icon: ClipboardDocumentListIcon },
    { name: 'Audit Logs', href: '/audit-logs', icon: ClockIcon, permission: { category: 'audit_logs', action: 'read' } },
    { name: 'User Activity', href: '/user-activity', icon: MapPinIcon, permission: { category: 'audit_logs', action: 'read' } },
    { name: 'Test Notification', href: '/test-notification', icon: BellAlertIcon, permission: { category: 'users', action: 'write' } },
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, permission: { category: 'settings', action: 'read' } },
  ], []);

  // Affiliate portal navigation (shown to affiliate role users)
  const portalNavigation = useMemo<NavItem[]>(() => [
    { name: 'Dashboard', href: '/portal', icon: HomeIcon },
    { name: 'Referrals', href: '/portal/referrals', icon: UserGroupIcon },
    { name: 'Commissions', href: '/portal/commissions', icon: BanknotesIcon },
    { name: 'Payouts', href: '/portal/payouts', icon: CurrencyDollarIcon },
    { name: 'Profile', href: '/portal/profile', icon: UserCircleIcon },
  ], []);

  const isAffiliate = role === 'affiliate';

  const filteredNavigation = useMemo(() => {
    if (isAffiliate) return portalNavigation;
    return navigation.filter(item => {
      if (!item.permission) return true;
      return hasPermission(item.permission.category as 'users' | 'businesses' | 'analytics' | 'payments' | 'access_codes' | 'settings' | 'audit_logs' | 'moderation' | 'tos' | 'email_templates' | 'blog' | 'promo_codes' | 'affiliates', item.permission.action);
    });
  }, [navigation, portalNavigation, hasPermission, isAffiliate]);

  return (
    <>
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          <span className="text-xl font-bold text-white">{isAffiliate ? 'Affiliate Portal' : 'LinkBy6 Admin'}</span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 text-gray-400 hover:text-white"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <nav className="mt-4 px-2 space-y-1">
          {filteredNavigation.map((item) => {
            const badgeCount = item.badgeKey ? counts[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-brand text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1">{item.name}</span>
                {badgeCount > 0 && (
                  <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:flex-col bg-gray-900 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          {!sidebarCollapsed && (
            <span className="text-xl font-bold text-white">{isAffiliate ? 'Affiliate Portal' : 'LinkBy6 Admin'}</span>
          )}
          {sidebarCollapsed && (
            <span className="text-xl font-bold text-white mx-auto">L6</span>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <ChevronLeftIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 mt-4 px-2 space-y-1 overflow-y-auto">
          {filteredNavigation.map((item) => {
            const badgeCount = item.badgeKey ? counts[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                title={sidebarCollapsed ? `${item.name}${badgeCount ? ` (${badgeCount})` : ''}` : undefined}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-brand text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`
                }
              >
                <div className="relative">
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarCollapsed && badgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-gray-900" />
                  )}
                </div>
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1">{item.name}</span>
                    {badgeCount > 0 && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Role badge */}
        <div className="p-4 border-t border-gray-800">
          {!sidebarCollapsed && role && (
            <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase">
              {role.replace('_', ' ')}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
